import { useState, useEffect } from "react";
import { useComponentTranslation } from "@/services/i18n";
import toast from "react-hot-toast";
import api from "@/services/api";
import useStore from "@/services/store";
import Loader from "@/components/loader";
import { FiMail, FiSave } from "react-icons/fi";

const translations = {
  en: {
    title: "Email Configuration",
    description: "Configure email sender settings for candidate communication",
    sender_name: "Sender Name",
    sender_name_placeholder: "Your Company Name",
    sender_name_help: "Name that will appear in the 'From' field",
    sender_email: "Sender Email",
    sender_email_placeholder: "noreply@yourcompany.com",
    sender_email_help: "Email address that will appear in the 'From' field",
    reply_to: "Reply-To Email",
    reply_to_placeholder: "contact@yourcompany.com",
    reply_to_help: "Email address for candidate replies",
    smtp_host: "SMTP Host",
    smtp_host_placeholder: "smtp.yourprovider.com",
    smtp_port: "SMTP Port",
    smtp_port_placeholder: "587",
    smtp_username: "SMTP Username",
    smtp_username_placeholder: "your-username",
    smtp_password: "SMTP Password",
    smtp_password_placeholder: "your-password",
    smtp_secure: "Use TLS/SSL",
    smtp_secure_help: "Enable secure connection for SMTP",
    save: "Save Configuration",
    saving: "Saving...",
    success: "Email configuration saved successfully",
    error: "Error saving email configuration",
    fetch_error: "Error fetching email configuration",
  },
  fr: {
    title: "Configuration Email",
    description: "Configurer les paramètres d'envoi d'emails pour la communication avec les candidats",
    sender_name: "Nom de l'expéditeur",
    sender_name_placeholder: "Nom de votre entreprise",
    sender_name_help: "Nom qui apparaîtra dans le champ 'De'",
    sender_email: "Email de l'expéditeur",
    sender_email_placeholder: "noreply@votreentreprise.com",
    sender_email_help: "Adresse email qui apparaîtra dans le champ 'De'",
    reply_to: "Email de réponse",
    reply_to_placeholder: "contact@votreentreprise.com",
    reply_to_help: "Adresse email pour les réponses des candidats",
    smtp_host: "Hôte SMTP",
    smtp_host_placeholder: "smtp.votrefournisseur.com",
    smtp_port: "Port SMTP",
    smtp_port_placeholder: "587",
    smtp_username: "Nom d'utilisateur SMTP",
    smtp_username_placeholder: "votre-nom-utilisateur",
    smtp_password: "Mot de passe SMTP",
    smtp_password_placeholder: "votre-mot-de-passe",
    smtp_secure: "Utiliser TLS/SSL",
    smtp_secure_help: "Activer la connexion sécurisée pour SMTP",
    save: "Enregistrer la configuration",
    saving: "Enregistrement...",
    success: "Configuration email enregistrée avec succès",
    error: "Erreur lors de l'enregistrement de la configuration email",
    fetch_error: "Erreur lors de la récupération de la configuration email",
  },
};

export default function Email() {
  const { t } = useComponentTranslation(translations);
  const { organization } = useStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({
    email_sender_name: "",
    email_sender_email: "",
    email_reply_to: "",
    smtp_host: "",
    smtp_port: "587",
    smtp_username: "",
    smtp_password: "",
    smtp_secure: true,
  });

  useEffect(() => {
    fetchConfiguration();
  }, [organization?._id]);

  const fetchConfiguration = async () => {
    try {
      setLoading(true);
      const { ok, data } = await api.post("/organization/me", { organization_id: organization?._id });
      if (ok && data) {
        setValues({
          email_sender_name: data.email_sender_name || "",
          email_sender_email: data.email_sender_email || "",
          email_reply_to: data.email_reply_to || "",
          smtp_host: data.smtp_host || "",
          smtp_port: data.smtp_port || "587",
          smtp_username: data.smtp_username || "",
          smtp_password: data.smtp_password || "",
          smtp_secure: data.smtp_secure !== undefined ? data.smtp_secure : true,
        });
      }
    } catch (e) {
      console.error("Error fetching email configuration:", e);
      toast.error(e.code);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await api.put("/organization", { ...values, organization_id: organization._id });
      if (response.ok) {
        toast.success(t("success"));
      } else {
        toast.error(response.code || "Error saving configuration");
      }
    } catch (e) {
      console.error("Error saving email configuration:", e);
      toast.error(e.code || e.message || "Error saving configuration");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="max-w-3xl">
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <FiMail className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t("title")}</h2>
            <p className="text-sm text-gray-600 mt-1">{t("description")}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Sender Information */}
          <div className="space-y-6">
            <h3 className="text-base font-semibold text-gray-900 pb-2 border-b border-gray-200">Sender Information</h3>
            
            {/* Sender Name */}
            <div>
              <label htmlFor="email_sender_name" className="input-label">
                {t("sender_name")}
              </label>
              <input
                type="text"
                id="email_sender_name"
                name="email_sender_name"
                value={values.email_sender_name}
                onChange={(e) => setValues({ ...values, email_sender_name: e.target.value })}
                placeholder={t("sender_name_placeholder")}
                className="input"
              />
              <p className="text-sm text-gray-500 mt-1">{t("sender_name_help")}</p>
            </div>

            {/* Sender Email */}
            <div>
              <label htmlFor="email_sender_email" className="input-label">
                {t("sender_email")}
              </label>
              <input
                type="email"
                id="email_sender_email"
                name="email_sender_email"
                value={values.email_sender_email}
                onChange={(e) => setValues({ ...values, email_sender_email: e.target.value })}
                placeholder={t("sender_email_placeholder")}
                className="input"
              />
              <p className="text-sm text-gray-500 mt-1">{t("sender_email_help")}</p>
            </div>

            {/* Reply-To Email */}
            <div>
              <label htmlFor="email_reply_to" className="input-label">
                {t("reply_to")}
              </label>
              <input
                type="email"
                id="email_reply_to"
                name="email_reply_to"
                value={values.email_reply_to}
                onChange={(e) => setValues({ ...values, email_reply_to: e.target.value })}
                placeholder={t("reply_to_placeholder")}
                className="input"
              />
              <p className="text-sm text-gray-500 mt-1">{t("reply_to_help")}</p>
            </div>
          </div>

          {/* SMTP Settings */}
          <div className="space-y-6">
            <h3 className="text-base font-semibold text-gray-900 pb-2 border-b border-gray-200">SMTP Settings</h3>

            {/* SMTP Host */}
            <div>
              <label htmlFor="smtp_host" className="input-label">
                {t("smtp_host")}
              </label>
              <input
                type="text"
                id="smtp_host"
                name="smtp_host"
                value={values.smtp_host}
                onChange={(e) => setValues({ ...values, smtp_host: e.target.value })}
                placeholder={t("smtp_host_placeholder")}
                className="input"
              />
            </div>

            {/* SMTP Port */}
            <div>
              <label htmlFor="smtp_port" className="input-label">
                {t("smtp_port")}
              </label>
              <input
                type="number"
                id="smtp_port"
                name="smtp_port"
                value={values.smtp_port}
                onChange={(e) => setValues({ ...values, smtp_port: e.target.value })}
                placeholder={t("smtp_port_placeholder")}
                className="input"
              />
            </div>

            {/* SMTP Username */}
            <div>
              <label htmlFor="smtp_username" className="input-label">
                {t("smtp_username")}
              </label>
              <input
                type="text"
                id="smtp_username"
                name="smtp_username"
                value={values.smtp_username}
                onChange={(e) => setValues({ ...values, smtp_username: e.target.value })}
                placeholder={t("smtp_username_placeholder")}
                className="input"
              />
            </div>

            {/* SMTP Password */}
            <div>
              <label htmlFor="smtp_password" className="input-label">
                {t("smtp_password")}
              </label>
              <input
                type="password"
                id="smtp_password"
                name="smtp_password"
                value={values.smtp_password}
                onChange={(e) => setValues({ ...values, smtp_password: e.target.value })}
                placeholder={t("smtp_password_placeholder")}
                className="input"
              />
            </div>

            {/* Use TLS/SSL */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="smtp_secure"
                  name="smtp_secure"
                  type="checkbox"
                  checked={values.smtp_secure}
                  onChange={(e) => setValues({ ...values, smtp_secure: e.target.checked })}
                  className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary"
                />
              </div>
              <div className="ml-3">
                <label htmlFor="smtp_secure" className="input-label mb-0">
                  {t("smtp_secure")}
                </label>
                <p className="text-sm text-gray-500 mt-1">{t("smtp_secure_help")}</p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end border-t border-gray-200 pt-6">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? (
                <>
                  <Loader size="small" color="white" />
                  <span>{t("saving")}</span>
                </>
              ) : (
                <>
                  <FiSave className="w-4 h-4" />
                  <span>{t("save")}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}






